// Copyright 2017 Sorama B.V.

import { HttpClient as FetchHttpClient } from "aurelia-fetch-client";
import { HttpResponseMessage, RequestBuilder, HttpClient as XhrHttpClient } from "aurelia-http-client";
import { ProgressResponse } from "datatypes/ProgressResponse";
import { PromiseReason } from "datatypes/PromiseExtension";
import { HttpResponseMessageError } from "errors/HttpResponseMessageError";
import { ProgressIndicator, ProgressType } from "libraries/ProgressIndicator/ProgressIndicator";
import {
    IWebRequest,
    TypedHttpResponseMessage,
    WebRequestPriority,
} from "libraries/WebRequestHandler/IWebRequestHandler";
import { WebRequestHandler } from "libraries/WebRequestHandler/WebRequestHandler";

//import { BSON } from "bson";  // We disable BSON for now

abstract class WebRequestBase<T> implements IWebRequest<T> {
    private static readonly BsonMimeType: string = "application/bson";

    //protected requestBuilder: RequestBuilder;
    private handler: WebRequestHandler;
    private xhrClient: XhrHttpClient;
    private fetchClient: FetchHttpClient;

    private allowBson: boolean;

    // Download Progress
    private hasDownloadProgress = false;
    private downloadProgressCallback: (progressIndicator: ProgressIndicator) => void;

    // Upload Progress
    private hasUploadProgress = false;
    private uploadProgressCallback: (progressIndicator: ProgressIndicator) => void;

    private abortSignal: AbortSignal;

    // Progress
    private hasProgress = false;
    private progressCallback: (progress: number) => void;

    // Priority
    private priority: WebRequestPriority = WebRequestPriority.NormalPriority;

    // State
    private stateObject: unknown = null;

    // Value type
    private valueType = false;

    // Binary Response
    private binaryResponse = false;

    // Binary Content
    private binaryContent = false;

    // BSON Encoding
    private bsonEncoding = false;

    // Allow caching of the response by the browser
    private allowCache = false;

    private _url: string;
    private requestType: string;
    protected body: unknown;
    protected bodyAsValue = false;

    private receivedBytes = 0;
    private lastProgress = 0;

    public get url(): string {
        return this._url;
    }

    constructor(
        handler: WebRequestHandler,
        xhrClient: XhrHttpClient,
        fetchClient: FetchHttpClient,
        allowBson: boolean,
        url: string,
        requestType: string,
        body?: unknown
    ) {
        this.xhrClient = xhrClient;
        this.fetchClient = fetchClient;
        this.handler = handler;
        this.allowBson = allowBson;
        this._url = url;
        this.requestType = requestType;
        this.body = body;
    }

    //#region IWebRequest

    public withDownloadProgress(callback: (progressIndicator: ProgressIndicator) => void): IWebRequest<T> {
        if (this.hasProgress) throw Error("Progress and download progress callbacks cannot be used simultaneously");

        this.hasDownloadProgress = true;
        this.downloadProgressCallback = callback;

        return this;
    }

    public withUploadProgress(callback: (progressIndicator: ProgressIndicator) => void): IWebRequest<T> {
        this.hasUploadProgress = true;
        this.uploadProgressCallback = callback;

        return this;
    }

    public withProgress(callback: (progress: number) => void): IWebRequest<T> {
        if (this.hasDownloadProgress)
            throw Error("Progress and download progress callbacks cannot be used simultaneously");

        this.hasProgress = true;
        this.progressCallback = callback;

        return this;
    }

    public withPriority(priority: WebRequestPriority): IWebRequest<T> {
        this.priority = priority;
        return this;
    }

    public withState(state: unknown): IWebRequest<T> {
        this.stateObject = state;
        return this;
    }

    public abstract withValue(): IWebRequest<T>;

    public withCache(): IWebRequest<T> {
        this.allowCache = true;
        return this;
    }

    public withBinaryResponse(): IWebRequest<Blob> {
        this.binaryResponse = true;

        return <IWebRequest<Blob>>(<unknown>this);
    }

    public withBinaryContent(): IWebRequest<T> {
        this.binaryContent = true;

        return this;
    }

    public withBSONResponse(): IWebRequest<T> {
        this.bsonEncoding = this.allowBson;

        return this;
    }

    public withAbortSignal(signal: AbortSignal): IWebRequest<T> {
        this.abortSignal = signal;
        return this;
    }

    public supportsProgressWithCurrentSettings(): boolean {
        return !this.bsonEncoding || !!this.fetchClient;
    }

    public withUrl(url: string): IWebRequest<T> {
        this._url = url;
        return this;
    }

    public send(): PromiseReason<TypedHttpResponseMessage<T>, HttpResponseMessageError> {
        // Determine whether to use fetch or xhr api
        if (!this.fetchClient) {
            if (this.abortSignal)
                console.warn("AbortSignal provided, but fetchClient is not available — abort will not work");
            return this.sendXhr();
        }

        // Prefer fetch if abortSignal is present
        if (this.abortSignal) return this.sendFetch();

        if (this.bsonEncoding && this.hasProgress) {
            // This option is only possible using fetch api, use fetch
            return this.sendFetch();
        }

        // Use xhr in all other cases for compatibility
        return this.sendXhr();
    }

    public sendXhr(): PromiseReason<TypedHttpResponseMessage<T>, HttpResponseMessageError> {
        // Check if properly configured
        if (this.hasProgress && this.hasDownloadProgress)
            throw Error("Progress and download progress callbacks cannot be used simultaneously");

        if (this.hasProgress && this.bsonEncoding) {
            // This combination is not supported by the XHR, disable progress
            this.hasProgress = false;
            this.progressCallback = undefined;
        }

        let requestBuilder: RequestBuilder = this.xhrClient.createRequest(this._url).withInterceptor(this.handler);

        if (!this.allowCache) requestBuilder = requestBuilder.withHeader("Cache-Control", "no-cache");

        switch (this.requestType.toLowerCase()) {
            case "get":
                requestBuilder = requestBuilder.asGet();
                break;
            case "post":
                requestBuilder = requestBuilder.asPost();
                break;
            case "put":
                requestBuilder = requestBuilder.asPut();
                break;
            case "delete":
                requestBuilder = requestBuilder.asDelete();
                break;
            case "head":
                requestBuilder = requestBuilder.asHead();
                break;
            default:
                throw new Error("Invalid Request Type");
        }

        if (this.body) {
            const body = this.bodyAsValue ? "=" + this.body : this.body;

            let contentType: string;
            if (typeof body == "string" || typeof body == "number") {
                contentType = "application/x-www-form-urlencoded";
            } else {
                contentType = "application/json";
            }

            requestBuilder = requestBuilder.withHeader("Content-Type", contentType).withContent(body);
        }

        if (this.hasDownloadProgress) {
            requestBuilder = requestBuilder.withDownloadProgressCallback((event: ProgressEvent) =>
                this.downloadProgressCallback(new ProgressIndicator(ProgressType.Download, event))
            );
        }

        if (this.hasUploadProgress) {
            requestBuilder = requestBuilder.withProgressCallback((event: ProgressEvent) =>
                this.uploadProgressCallback(new ProgressIndicator(ProgressType.Upload, event))
            );
        }

        if (this.hasProgress) {
            requestBuilder = requestBuilder.withDownloadProgressCallback((event: ProgressEvent) => {
                const indicator = new ProgressIndicator(ProgressType.Download, event);
                this.updateProgressJson(indicator, this.progressCallback);
            });
        }

        if (this.binaryResponse) {
            requestBuilder = requestBuilder.withResponseType("blob");
        }

        if (this.binaryContent) {
            requestBuilder.withHeader("Content-Type", "application/octet-stream");
        }

        if (this.bsonEncoding) {
            requestBuilder = requestBuilder.withHeader("Accept", WebRequestBase.BsonMimeType);
            if (!this.binaryResponse) {
                requestBuilder = requestBuilder.withResponseType("arraybuffer");
            }
        } else {
            requestBuilder = requestBuilder.withHeader("Accept", "*/*");
        }

        return <PromiseReason<TypedHttpResponseMessage<T>, HttpResponseMessageError>>this.handler
            .postXhrRequest(requestBuilder, this.priority)
            .then((response: TypedHttpResponseMessage<T>) => {
                if (this.hasProgress) {
                    // Strip of progress json stuff
                    response = this.onProgressResponseCompleted<T>(response);
                }

                // Add state object
                response.state = this.stateObject;

                // Decode bson if required
                if (
                    response.responseType == WebRequestBase.BsonMimeType ||
                    (this.bsonEncoding && response.responseType == "arraybuffer")
                ) {
                    throw new Error("BSON not supported");
                    // let bson = new BSON();
                    // let content = <unknown>bson.deserialize(Buffer.from(<ArrayBuffer><unknown>response.response));

                    // if (content[0] !== undefined && !content.length)
                    // {
                    //     // Convert to an actual array..
                    //     let arrayContent = [];
                    //     for (let i = 0; content[i] !== undefined; i++)
                    //     {
                    //         arrayContent.push(content[i]);
                    //     }

                    //     content = <T><unknown>arrayContent;
                    // }

                    // response = <TypedHttpResponseMessage<T>>{
                    //     content: content,
                    //     headers: response.headers,
                    //     isSuccess: response.isSuccess,
                    //     mimeType: response.mimeType,
                    //     requestMessage: response.requestMessage,
                    //     response: response.response,
                    //     responseType: response.responseType,
                    //     reviver: response.reviver,
                    //     state: response.state,
                    //     statusCode: response.statusCode,
                    //     statusText: response.statusText
                    // };
                }

                return response;
            })
            .catch((response: HttpResponseMessage) => {
                throw HttpResponseMessageError.fromHttpResponseMessage(response);
            });
    }

    public sendFetch(): PromiseReason<TypedHttpResponseMessage<T>, HttpResponseMessageError> {
        const requestHeaders = new Headers();
        let requestBody: unknown;

        if (this.body) {
            const body = this.bodyAsValue ? "=" + this.body : this.body;

            let contentType: string;
            if (typeof body == "string" || typeof body == "number") {
                contentType = "application/x-www-form-urlencoded";
                requestBody = body;
            } else {
                contentType = "application/json";
                requestBody = JSON.stringify(body);
            }

            requestHeaders.append("Content-Type", contentType);
        }

        if (this.bsonEncoding) {
            requestHeaders.append("Accept", WebRequestBase.BsonMimeType);
        } else {
            requestHeaders.append("Accept", "*/*");
        }

        const requestInit = <unknown>{
            method: this.requestType.toUpperCase(),
            headers: requestHeaders,
            body: requestBody,
            signal: this.abortSignal,
        };

        const request = new Request(this._url, requestInit);

        return <PromiseReason<TypedHttpResponseMessage<T>, HttpResponseMessageError>>(
            this.handler
                .postFetchRequest(request, this.priority, this.hasProgress ? this.progressCallback : undefined)
                .then((response) => {
                    const forProgress = response.clone();
                    const forJson = response.clone();

                    if (this.bsonEncoding) {
                        const reader = forProgress.body.getReader(); // Read the body as a stream
                        return this.readBsonProgressAndResult(reader, response).then((obj: T) => {
                            return <TypedHttpResponseMessage<T>>(<unknown>{
                                content: obj,
                                response: "",
                                statusCode: response.status,
                                statusText: response.statusText,
                                isSuccess: response.status >= 200 && response.status < 300,
                                mimeType: response.type,
                                state: this.stateObject,
                                requestMessage: <Request>undefined,
                                reviver: undefined,
                            });
                        });
                    }

                    // Read response body as JSON from the cloned response
                    return forJson.json().then((x) => {
                        return <TypedHttpResponseMessage<T>>(<unknown>{
                            content: x.result,
                            response: "",
                            statusCode: response.status,
                            statusText: response.statusText,
                            isSuccess: response.status >= 200 && response.status < 300,
                            mimeType: response.type,
                            state: this.stateObject,
                            requestMessage: <Request>undefined,
                            reviver: undefined,
                        });
                    });
                })
        );
    }

    //#endregion

    private readBsonProgressAndResult(reader: ReadableStreamReader<Uint8Array>, response: Response): Promise<T> {
        throw new Error("BSON not supported");
        // return reader.read().then((chunk: { value: Uint8Array, done: boolean }) =>
        // {
        //     // Parse as BSON
        //     if (!this.updateProgressBson(chunk))
        //     {
        //         return this.readBsonProgressAndResult(reader, response);
        //     }
        //     else
        //     {
        //         // Read result
        //         reader.releaseLock();
        //         return response.arrayBuffer().then(x =>
        //         {
        //             const objects = <unknown>new Array(102);
        //             let bsonParser = <unknown>new BSON();

        //             bsonParser.deserializeStream(Buffer.from(x), 0, 102, objects, 0);

        //             let resultObject = objects[objects.length - 1];
        //             if (resultObject.error)
        //             {
        //                 throw <HttpResponseMessageError>
        //                 {
        //                     name: resultObject.error.name,
        //                     message: resultObject.error.message,
        //                     statusCode: 500
        //                 };
        //             }
        //             else if (resultObject.result)
        //                 return <T>resultObject.result;
        //             else
        //                 throw new Error("Server responded with an empty response");
        //         });
        //     }
        // });
    }

    private updateProgressJson<T>(progressIndicator: ProgressIndicator, onProgress: (progress: number) => void): void {
        if (this.lastProgress >= 0.99) {
            // Ignore any update when the last progress we got was above 99%, otherwise were just slowing the call down
            return;
        }

        if (progressIndicator.progresType == ProgressType.Upload) return; // Don't care about that

        let responseTarget = <XMLHttpRequest>(<unknown>progressIndicator.progressEvent.srcElement);
        if (!responseTarget) responseTarget = <XMLHttpRequest>(<unknown>progressIndicator.progressEvent.target);

        let currentResponse = <string>responseTarget.response;

        // Close the current response tags to create a valid JSON object
        currentResponse += "]}";

        // Parse as JSON
        try {
            const progressJson = <ProgressResponse<T>>JSON.parse(currentResponse);

            if (!progressJson.progress || progressJson.progress.length === 0) return; // No progress

            const newProgress = progressJson.progress[progressJson.progress.length - 1];
            if (newProgress > this.lastProgress) {
                onProgress(newProgress);
                this.lastProgress = newProgress;
            }
        } catch (e) {
            // Ignore errors
        }
    }

    private updateProgressBson<T>(chunk: { value: Uint8Array; done: boolean }): boolean {
        throw new Error("BSON not supported");
        // if (chunk.done)
        //     return true;

        // // Parse as BSON
        // let bson = new BSON();

        // try
        // {
        //     let buffer = Buffer.from(<unknown>chunk.value.buffer);
        //     let bsonParser = <unknown>new BSON();

        //     const prog = <{ p: number }[]>[{ p: 0 }];

        //     // Keep on deserializing 1 element untill done
        //     let index = 0;
        //     while (index < buffer.length)
        //     {
        //         index = bsonParser.deserializeStream(buffer, index, 1, prog, 0);

        //         if (prog[0].p >= 1)
        //         {
        //             this.progressCallback(1);
        //             return true;
        //         }
        //     }

        //     this.progressCallback(prog[0].p);

        //     return false;
        // }
        // catch(e)
        // {
        //     // Just return true and ignore rest of the progres
        //     return true;
        // }
    }

    private onProgressResponseCompleted<T>(responseMessage: HttpResponseMessage): TypedHttpResponseMessage<T> {
        const response = <ProgressResponse<T>>responseMessage.content;
        if (response.error) {
            // Error occurred, reject
            throw <HttpResponseMessageError>{
                name: response.error.name,
                message: response.error.message,
                statusCode: 500,
            };
        } else if (response.result) {
            return <TypedHttpResponseMessage<T>>{
                content: response.result,
                statusCode: responseMessage.statusCode,
                statusText: responseMessage.statusText,
                headers: responseMessage.headers,
                isSuccess: responseMessage.isSuccess,
                mimeType: responseMessage.mimeType,
                requestMessage: responseMessage.requestMessage,
                responseType: responseMessage.responseType,
                reviver: responseMessage.reviver,
            };
        } else {
            // No response and no error, apparently not a progress response, return the object itself
            return <TypedHttpResponseMessage<T>>responseMessage;
        }
    }
}

abstract class WebRequestWithBody<T> extends WebRequestBase<T> {
    constructor(
        handler: WebRequestHandler,
        xhrClient: XhrHttpClient,
        fetchClient: FetchHttpClient,
        allowBson: boolean,
        url: string,
        responseType: string,
        body?: unknown
    ) {
        super(handler, xhrClient, fetchClient, allowBson, url, responseType, body);
    }

    public withValue(): IWebRequest<T> {
        if (this.body === undefined || this.body === null || this.body === "")
            throw new Error("Request does not have a body");

        this.bodyAsValue = true;
        return this;
    }
}

export class GetWebRequest<T> extends WebRequestBase<T> {
    constructor(
        handler: WebRequestHandler,
        xhrClient: XhrHttpClient,
        fetchClient: FetchHttpClient,
        allowBson: boolean,
        url: string
    ) {
        super(handler, xhrClient, fetchClient, allowBson, url, "get");
    }

    public withValue(): IWebRequest<T> {
        throw new Error("Request body is not supported for 'GET' requests");
    }
}

export class HeadWebRequest<T> extends WebRequestBase<T> {
    constructor(
        handler: WebRequestHandler,
        xhrClient: XhrHttpClient,
        fetchClient: FetchHttpClient,
        allowBson: boolean,
        url: string
    ) {
        super(handler, xhrClient, fetchClient, allowBson, url, "head");
    }

    public withValue(): IWebRequest<T> {
        throw new Error("Request body is not supported for 'HEAD' requests");
    }
}

export class PostWebRequest<T> extends WebRequestWithBody<T> {
    constructor(
        handler: WebRequestHandler,
        xhrClient: XhrHttpClient,
        fetchClient: FetchHttpClient,
        allowBson: boolean,
        url: string,
        body?: unknown
    ) {
        super(handler, xhrClient, fetchClient, allowBson, url, "post", body);
    }
}

export class PutWebRequest<T> extends WebRequestWithBody<T> {
    constructor(
        handler: WebRequestHandler,
        xhrClient: XhrHttpClient,
        fetchClient: FetchHttpClient,
        allowBson: boolean,
        url: string,
        body?: unknown
    ) {
        super(handler, xhrClient, fetchClient, allowBson, url, "put", body);
    }
}

export class DeleteWebRequest<T> extends WebRequestWithBody<T> {
    constructor(
        handler: WebRequestHandler,
        xhrClient: XhrHttpClient,
        fetchClient: FetchHttpClient,
        allowBson: boolean,
        url: string,
        body?: unknown
    ) {
        super(handler, xhrClient, fetchClient, allowBson, url, "delete", body);
    }
}
